import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import * as echarts from 'echarts'
import type { EChartsOption } from 'echarts'
import { EChartsWrapper, buildThemeColors } from './EChartsWrapper'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { AlertTriangle, Clock, CalendarClock, ShieldAlert, BarChart3, Table2, Columns2, LayoutList } from 'lucide-react'
import api from '@/lib/api/axios'
import { escapeHtml } from '@/lib/escape-html'

// Types
interface DataStoreRecord {
    description: string
    vaultDatetime: string | null
    analytics: number | null
    objectSecondary: string
    sourceId: string
    /** The newest version with status 4, "No confirmed detections" - marked on the bar. Never called
     *  "clean" or a recommendation: the scan report is decision support. */
    recommendedGreenId: string | null
    recommendedGreenDateTime: string | null
    recommendedGreenSecondary?: string | null
}

interface ReconciliationChartProps {
    mainId: string
    scenario?: number | null
    onDataStoreClick?: (sourceId: string) => void
}

interface VersionRecord {
    sourceId: string
    description: string
    latestId: number
    latestDatetime: string | null
    latestSecondary: string
    latestAnalytics: number | null
    reconciliationDatetime: string | null
    recommendedId: number
    recommendedDatetime: string | null
    recommendedSecondary: string
    recommendedAnalytics: number | null
}

type ViewMode = 'chart' | 'chartTable' | 'table' | 'comparison'

// Fetch functions
async function fetchReconciliationData(mainId: string, scenario?: number | null): Promise<DataStoreRecord[]> {
    const params: Record<string, string> = {}
    if (scenario) params.scenario = String(scenario)
    const response = await api.get(`/api/recovery/reconciliation/${encodeURIComponent(mainId)}`, { params })
    return response.data?.data || response.data || []
}

async function fetchVersionData(mainId: string, scenario?: number | null): Promise<VersionRecord[]> {
    const params: Record<string, string> = {}
    if (scenario) params.scenario = String(scenario)
    const response = await api.get(`/api/recovery/reconciliation/${encodeURIComponent(mainId)}/versions`, { params })
    return response.data?.data || response.data || []
}

function formatHoursMinutes(totalMinutes: number): string {
    const hours = Math.floor(Math.abs(totalMinutes) / 60)
    const mins = Math.round(Math.abs(totalMinutes) % 60)
    if (hours === 0) return `${mins}m`
    if (mins === 0) return `${hours}h`
    return `${hours}h ${mins}m`
}

function formatDate(d: Date, locale?: string): string {
    const loc = locale || 'en-GB'
    const mon = d.toLocaleDateString(loc, { month: 'short' })
    const day = d.getDate().toString().padStart(2, '0')
    const hh = d.getHours().toString().padStart(2, '0')
    const mm = d.getMinutes().toString().padStart(2, '0')
    const ss = d.getSeconds().toString().padStart(2, '0')
    return `${day} ${mon} ${hh}:${mm}:${ss}`
}

/** "2d 3h 4m" - how far a moment is from another. */
function formatGap(ms: number): string {
    const secs = Math.floor(Math.abs(ms) / 1000)
    const d = Math.floor(secs / 86400)
    const h = Math.floor((secs % 86400) / 3600)
    const m = Math.floor((secs % 3600) / 60)
    const parts = [d && `${d}d`, h && `${h}h`, (m || (!d && !h)) && `${m}m`].filter(Boolean)
    return parts.join(' ')
}

// Text for the HTML tooltips: descriptions and version types come from the database (escapeHtml).
const esc = escapeHtml

/** Its own line, in neutral slate: a status-4 version is not a clean bill of health. */
const NO_CONFIRMED_MARKER = '#475569'

function parseTimestamp(raw: any): number {
    if (!raw || raw === '{}') return 0
    if (typeof raw === 'string') {
        let ts = new Date(raw).getTime()
        if (isNaN(ts) && raw.includes(' ')) {
            ts = new Date(raw.replace(' ', 'T') + 'Z').getTime()
        }
        return isNaN(ts) ? 0 : ts
    }
    if (typeof raw === 'number') return raw
    if (raw instanceof Date) return raw.getTime()
    return 0
}

// Custom renderItem for drawing bars from timestamp A to timestamp B
// position: 'left' | 'right' | 'center' — positions the bar within the category band
// roundTop: only round the top corners (for topmost segment), roundBottom: only round bottom corners
function makeRenderItem(
    color1: string,
    color2: string,
    position: 'center' | 'left' | 'right' = 'center',
    corners: 'all' | 'top' | 'bottom' | 'none' = 'all'
) {
    return function (params: any, api: any) {
        const catIdx = api.value(0)
        const low = api.value(1)
        const high = api.value(2)

        if (high <= low) return null

        const lowPx = api.coord([catIdx, low])
        const highPx = api.coord([catIdx, high])

        const bandWidth = api.size([1, 0])[0]

        let barWidth: number
        let barX: number

        if (position === 'center') {
            barWidth = bandWidth * 0.45
            barX = lowPx[0] - barWidth / 2
        } else {
            barWidth = bandWidth * 0.2
            const gap = bandWidth * 0.02
            if (position === 'left') {
                barX = lowPx[0] - barWidth - gap / 2
            } else {
                barX = lowPx[0] + gap / 2
            }
        }

        // r: [topLeft, topRight, bottomRight, bottomLeft]
        const R = 4
        let r: number[]
        switch (corners) {
            case 'top':    r = [R, R, 0, 0]; break
            case 'bottom': r = [0, 0, R, R]; break
            case 'none':   r = [0, 0, 0, 0]; break
            default:       r = [R, R, R, R]; break
        }

        return {
            type: 'rect',
            shape: {
                x: barX,
                y: Math.min(lowPx[1], highPx[1]),
                width: barWidth,
                height: Math.abs(lowPx[1] - highPx[1]),
                r,
            },
            style: {
                fill: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    { offset: 0, color: color1 },
                    { offset: 1, color: color2 },
                ]),
            },
        }
    }
}

export function ReconciliationChart({ mainId, scenario, onDataStoreClick }: ReconciliationChartProps) {
    const { t, i18n } = useTranslation()
    const locale = i18n.language || 'en-GB'
    const fmt = (d: Date) => formatDate(d, locale)
    const [viewMode, setViewMode] = useState<ViewMode>('chart')

    const { data, isLoading, error } = useQuery({
        queryKey: ['reconciliation', mainId, scenario],
        queryFn: () => fetchReconciliationData(mainId, scenario),
        enabled: !!mainId,
    })

    const { data: versionsData } = useQuery({
        queryKey: ['reconciliation-versions', mainId, scenario],
        queryFn: () => fetchVersionData(mainId, scenario),
        enabled: !!mainId,
    })

    // Compute chart data and summary values
    const chartData = useMemo(() => {
        if (!data || data.length === 0) return null

        const records = data
            .map((d) => ({
                ...d,
                timestamp: parseTimestamp(d.vaultDatetime),
                noConfirmedTs: parseTimestamp(d.recommendedGreenDateTime),
            }))
            .filter((d) => d.timestamp > 0)

        if (records.length === 0) return null

        const timestamps = records.map((r) => r.timestamp)
        // The axis also reaches back to the oldest "no confirmed detections" marker.
        const markerTimestamps = records.map((r) => r.noConfirmedTs).filter((ts) => ts > 0)
        const axisMinTs = Math.min(...timestamps, ...markerTimestamps)
        const minTs = Math.min(...timestamps)
        const maxTs = Math.max(...timestamps)
        const reconciliationPoint = minTs
        const latestBackup = maxTs
        const gapMs = maxTs - minTs
        const gapHours = gapMs / (1000 * 60 * 60)

        // Neutral disposition counts — no readiness verdict is derived.
        const counts = {
            total: records.length,
            detections: records.filter((r) => r.analytics === 2).length,
            indicators: records.filter((r) => r.analytics === 5).length,
            noConfirmed: records.filter((r) => r.analytics === 4).length,
        }

        // Padding below the earliest timestamp
        const paddingMs = Math.max(gapMs * 0.3, 3600000)
        const baseline = axisMinTs - paddingMs
        // Padding above the latest timestamp
        const ceiling = maxTs + Math.max(gapMs * 0.1, 1800000)

        return {
            records,
            reconciliationPoint,
            latestBackup,
            gapHours,
            counts,
            baseline,
            ceiling,
        }
    }, [data])

    // Build ECharts option using custom series
    const option = useMemo((): EChartsOption => {
        if (!chartData) return {}

        const colors = buildThemeColors()
        const { records, reconciliationPoint, baseline, ceiling } = chartData

        const categories = records.map((r) => r.description)

        const getStatusLabel = (analytics: number | null) => {
            if (analytics === 4) return t('recovery.reconciliation.clean', 'No confirmed detections')
            if (analytics === 5) return t('recovery.reconciliation.indicators', 'Indicators')
            if (analytics === 3) return t('recovery.reconciliation.suspicious', 'Suspicious')
            if (analytics === 2) return t('recovery.reconciliation.detections', 'Detections')
            if (analytics === 1) return t('recovery.reconciliation.inProgress', 'In Progress')
            if (analytics === 10) return t('recovery.reconciliation.failed', 'Failed')
            if (analytics === 0) return t('recovery.reconciliation.notRun', 'Not Run')
            return t('recovery.reconciliation.unscanned', 'Unscanned')
        }

        // Each datastore renders a single centered bar of its own latest backup
        // relative to the shared reconciliation point. The portion below the
        // reconciliation point is the reconciled span; the amber portion above is
        // the data beyond the reconciliation point. This is timeline data only —
        // no version is singled out as a restore target.

        // Below recon: reconciled span
        const reconciledData = records.map((_r, i) =>
            [i, baseline, reconciliationPoint]
        )
        // Above recon: amber (data beyond the reconciliation point)
        const aboveReconData = records.map((r, i) =>
            r.timestamp > reconciliationPoint
                ? [i, reconciliationPoint, r.timestamp]
                : [i, reconciliationPoint, reconciliationPoint]
        )

        // Pastel colours for the bars
        const successHex = '#86efac'    // pastel green (top of gradient) — reconciled span
        const successDarkHex = '#4ade80' // slightly deeper green (bottom)
        const amberHex = '#fcd34d'       // pastel amber (top)
        const amberDarkHex = '#fbbf24'   // slightly deeper amber (bottom)

        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'line', lineStyle: { color: colors.border, type: 'dashed' } },
                formatter: (params: any) => {
                    const items = Array.isArray(params) ? params : [params]
                    const item = items.find((p: any) => p.seriesName && !p.seriesName.startsWith('_'))
                    if (!item) return ''
                    const idx = item.dataIndex
                    const record = records[idx]
                    if (!record) return ''
                    const ts = new Date(record.timestamp)
                    const diffMs = record.timestamp - reconciliationPoint
                    const isBottleneck = record.timestamp === reconciliationPoint
                    // Format diff as days, hours, minutes, seconds
                    const diffTotalSecs = Math.floor(diffMs / 1000)
                    const diffDays = Math.floor(diffTotalSecs / 86400)
                    const diffHrs = Math.floor((diffTotalSecs % 86400) / 3600)
                    const diffMins = Math.floor((diffTotalSecs % 3600) / 60)
                    const diffSecs = diffTotalSecs % 60
                    let diffStr = ''
                    if (diffDays > 0) diffStr += `${diffDays}d `
                    if (diffHrs > 0) diffStr += `${diffHrs}h `
                    if (diffMins > 0) diffStr += `${diffMins}m `
                    if (diffSecs > 0 || diffStr === '') diffStr += `${diffSecs}s`
                    diffStr = diffStr.trim()

                    // Status circle colors - match app's analytics-pulse-ring-red animation.
                    // Code 4 ("No confirmed detections") is NEUTRAL slate, not green —
                    // it is not a clean bill of health. Code 2 = red (detections),
                    // code 5 = amber (indicators).
                    const statusColor = record.analytics === 2 ? '#ef4444' : record.analytics === 5 ? '#fbbf24' : record.analytics === 4 ? '#94a3b8' : '#a3a3a3'
                    const pulseStyle = record.analytics === 2
                        ? 'animation: analytics-pulse-ring-red 1.5s ease-out infinite;'
                        : ''
                    // Inject the same keyframes used in globals.css
                    const pulseKeyframes = record.analytics === 2
                        ? '<style>@keyframes analytics-pulse-ring-red { 0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.6); } 70% { box-shadow: 0 0 0 12px rgba(239, 68, 68, 0); } 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); } }</style>'
                        : ''

                    // The newest version with no confirmed detections, and where it sits against the
                    // reconciliation point. Stated as a fact, not a recommendation.
                    const nc = record.noConfirmedTs
                    const ncWhere = !nc ? ''
                        : nc === reconciliationPoint ? t('recovery.reconciliation.atPoint', 'At the reconciliation point')
                            : nc < reconciliationPoint
                                ? t('recovery.reconciliation.beforePoint', '{{gap}} before the reconciliation point', { gap: formatGap(reconciliationPoint - nc) })
                                : t('recovery.reconciliation.afterPoint', '{{gap}} after the reconciliation point', { gap: formatGap(nc - reconciliationPoint) })
                    const ncLine = nc
                        ? `<div style="display:flex;align-items:center;gap:6px;margin-top:6px">
                               <span style="display:inline-block;width:9px;height:9px;transform:rotate(45deg);background:${NO_CONFIRMED_MARKER}"></span>
                               <span>${t('recovery.reconciliation.latestNoConfirmed', 'Latest with no confirmed detections')}: ${record.recommendedGreenSecondary ? esc(record.recommendedGreenSecondary) + ' ' : ''}${fmt(new Date(nc))}</span>
                           </div>
                           <div style="margin-left:15px;font-size:11px;color:#888">${ncWhere}</div>`
                        : `<div style="margin-top:6px;font-size:12px;color:#888">${t('recovery.reconciliation.latestNoConfirmed', 'Latest with no confirmed detections')}: ${t('recovery.reconciliation.latestNoConfirmedNone', 'None: no version of this store has a scan with no confirmed detections')}</div>`

                    return `${pulseKeyframes}<div style="padding:6px 10px">
                        <div style="font-weight:600;margin-bottom:2px;font-size:14px">${esc(record.description)}</div>
                        ${record.objectSecondary ? `<div style="margin-bottom:6px;color:#888;font-size:12px">${esc(record.objectSecondary)}</div>` : '<div style="margin-bottom:6px"></div>'}
                        <div style="margin-bottom:4px">${t('recovery.reconciliation.latestBackup', 'Latest Backup')}: ${fmt(ts)}</div>
                        <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px">
                            ${t('recovery.reconciliation.scanStatus', 'Scan Status')}:
                            <span style="display:inline-flex;align-items:center;gap:5px">
                                <span style="display:inline-block;width:12px;height:12px;border-radius:50%;background:${statusColor};${pulseStyle}"></span>
                                ${getStatusLabel(record.analytics)}
                            </span>
                        </div>
                        <div style="margin-top:6px;font-style:italic;color:#888">${
                            isBottleneck
                                ? t('recovery.reconciliation.definesPoint', 'This store defines the reconciliation point')
                                : `${diffStr} ${t('recovery.reconciliation.beyondPoint', 'beyond reconciliation point')}`
                        }</div>
                        ${!isBottleneck ? `<div style="margin-top:2px;font-size:11px;color:#aaa">${t('recovery.reconciliation.reconPointSetBy', 'Reconciliation point set by')}: ${esc(records.find((r) => r.timestamp === reconciliationPoint)?.description || '-')}</div>` : ''}
                        ${ncLine}
                    </div>`
                },
            },
            grid: {
                left: 20,
                right: 40,
                top: 40,
                bottom: 60,
                containLabel: true,
            },
            legend: {
                show: true,
                bottom: 0,
                textStyle: { color: colors.mutedForeground, fontSize: 11 },
                data: [
                    { name: t('recovery.reconciliation.reconciledLabel', 'Reconciled'), icon: 'roundRect' },
                    { name: t('recovery.reconciliation.unreconciledLabel', 'Unreconciled gap'), icon: 'roundRect' },
                    ...(records.some((r) => r.noConfirmedTs > 0)
                        ? [{ name: t('recovery.reconciliation.latestNoConfirmed', 'Latest with no confirmed detections'), icon: 'diamond' }]
                        : []),
                ],
            },
            xAxis: {
                type: 'category',
                data: categories,
                axisLabel: {
                    color: colors.mutedForeground,
                    rotate: categories.length > 6 ? 30 : 0,
                    fontSize: 11,
                },
                axisLine: { lineStyle: { color: colors.border } },
                axisTick: { lineStyle: { color: colors.border } },
            },
            yAxis: {
                type: 'value',
                min: baseline,
                max: ceiling,
                axisLabel: {
                    color: colors.mutedForeground,
                    fontSize: 11,
                    formatter: (val: number) => fmt(new Date(val)),
                },
                axisPointer: {
                    label: {
                        formatter: (params: any) => fmt(new Date(params.value)),
                    },
                },
                axisLine: { show: false },
                splitLine: { lineStyle: { color: colors.border, type: 'dashed' } },
            },
            series: [
                // Reconciled span below the reconciliation point (bottom corners rounded)
                {
                    name: t('recovery.reconciliation.reconciledLabel', 'Reconciled'),
                    type: 'custom',
                    renderItem: makeRenderItem(successHex, successDarkHex, 'center', 'bottom') as any,
                    itemStyle: { color: successHex },
                    encode: { x: 0, y: [1, 2] },
                    data: reconciledData,
                    z: 2,
                },
                // Amber above reconciliation point — data beyond the point (top corners rounded)
                {
                    name: t('recovery.reconciliation.unreconciledLabel', 'Unreconciled gap'),
                    type: 'custom',
                    renderItem: makeRenderItem(amberHex, amberDarkHex, 'center', 'top') as any,
                    itemStyle: { color: amberHex },
                    encode: { x: 0, y: [1, 2] },
                    data: aboveReconData,
                    z: 2,
                },
                // On-bar labels: gap beyond the reconciliation point, or "recovery point"
                // for the store(s) that define it — so the chart is readable without hovering.
                {
                    name: '_gap_labels',
                    type: 'custom',
                    silent: true,
                    renderItem: ((_params: any, apiObj: any) => {
                        const catIdx = apiObj.value(0)
                        const ts = apiObj.value(1)
                        const pt = apiObj.coord([catIdx, ts])
                        const rec = records[catIdx]
                        const label = `${rec?.objectSecondary ? rec.objectSecondary + ' ' : ''}${fmt(new Date(ts))}`
                        return {
                            type: 'text',
                            style: {
                                text: label,
                                x: pt[0],
                                y: pt[1] - 6,
                                textAlign: 'center',
                                textVerticalAlign: 'bottom',
                                fill: colors.foreground,
                                fontWeight: 600,
                                fontSize: 11,
                            },
                        }
                    }) as any,
                    encode: { x: 0, y: 1 },
                    data: records.map((r, i) => [i, r.timestamp]),
                    z: 5,
                },
                // The newest version with no confirmed detections, per store: a slate diamond on its
                // bar, at that version's time. Stores without one have no marker (the tooltip says so).
                {
                    name: t('recovery.reconciliation.latestNoConfirmed', 'Latest with no confirmed detections'),
                    type: 'scatter',
                    symbol: 'diamond',
                    symbolSize: 14,
                    itemStyle: { color: NO_CONFIRMED_MARKER, borderColor: '#ffffff', borderWidth: 1.5 },
                    data: records.map((r, i) => (r.noConfirmedTs > 0 ? [i, r.noConfirmedTs] : [i, null])),
                    z: 6,
                },
                // Invisible helper series for markLine (reconciliation point line)
                {
                    name: '_recon_line',
                    type: 'line',
                    data: [],
                    markLine: {
                        silent: true,
                        symbol: 'none',
                        lineStyle: {
                            color: colors.destructive || '#ef4444',
                            type: 'dashed',
                            width: 2,
                        },
                        label: { show: false },
                        data: [{ yAxis: reconciliationPoint }],
                    },
                },
            ],
        }
    }, [chartData, t, locale])

    // Event handlers
    const onEvents = useMemo(
        () => ({
            click: (params: any) => {
                if (params.dataIndex !== undefined && chartData) {
                    const record = chartData.records[params.dataIndex]
                    if (record && onDataStoreClick) {
                        onDataStoreClick(record.sourceId)
                    }
                }
            },
        }),
        [chartData, onDataStoreClick]
    )

    // Compute per-version disposition stats (must be before early returns - hooks rule).
    // Neutral counts only — no readiness verdict / restore recommendation is derived.
    const versionStats = useMemo(() => {
        if (!versionsData || versionsData.length === 0) return null
        const timestamps = versionsData.map((v) => parseTimestamp(v.recommendedDatetime)).filter((t) => t > 0)
        if (timestamps.length === 0) return null
        const reconTs = parseTimestamp(versionsData[0]?.reconciliationDatetime)
        const minTs = Math.min(...timestamps)
        const maxTs = Math.max(...timestamps)
        const gapMs = maxTs - minTs
        const gapH = gapMs / (1000 * 60 * 60)
        const counts = {
            total: versionsData.length,
            detections: versionsData.filter((v) => v.recommendedAnalytics === 2).length,
            indicators: versionsData.filter((v) => v.recommendedAnalytics === 5).length,
            noConfirmed: versionsData.filter((v) => v.recommendedAnalytics === 4).length,
        }
        return { reconciliationPoint: reconTs, latestBackup: maxTs, gapHours: gapH, counts }
    }, [versionsData])

    if (error) {
        return (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
                {t('common.errorLoadingData', 'Error loading data')}
            </div>
        )
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
                {t('common.loading', 'Loading...')}
            </div>
        )
    }

    if (!chartData || chartData.records.length === 0) {
        return (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
                {t('recovery.reconciliation.noData', 'No data store information available')}
            </div>
        )
    }

    if (chartData.records.length < 2) {
        return (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
                {t('recovery.reconciliation.singleDatastore', 'This object does not have multiple data stores. The reconciliation chart requires at least two data stores to compare.')}
            </div>
        )
    }

    const { reconciliationPoint, latestBackup, gapHours, counts } = chartData

    type DispositionCounts = { total: number, detections: number, indicators: number, noConfirmed: number }

    // Reusable summary cards renderer.
    // The final card shows NEUTRAL disposition counts only — no readiness/restore verdict.
    const renderSummaryCards = (stats: { reconciliationPoint: number, latestBackup: number, gapHours: number, counts: DispositionCounts }, compact = false) => {
        const cardClass = compact ? 'p-2' : 'p-4'
        const titleClass = compact ? 'text-[9px]' : 'text-xs'
        const valueClass = compact ? 'text-xs font-bold' : 'text-lg font-bold'
        const iconClass = compact ? 'h-3.5 w-3.5' : 'h-5 w-5'

        return (
            <div className={`grid gap-1.5 ${compact ? 'grid-cols-4' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'}`}>
                <Card className="bg-muted/50">
                    <CardContent className={cardClass}>
                        <div className="flex items-center gap-2">
                            <CalendarClock className={`${iconClass} text-muted-foreground shrink-0`} />
                            <div>
                                <p className={`${titleClass} font-medium text-muted-foreground uppercase tracking-wide`}>
                                    {t('recovery.reconciliation.reconciliationPoint', 'Reconciliation Point')}
                                </p>
                                <p className={valueClass}>{fmt(new Date(stats.reconciliationPoint))}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-muted/50">
                    <CardContent className={cardClass}>
                        <div className="flex items-center gap-2">
                            <AlertTriangle className={`${iconClass} text-muted-foreground shrink-0`} />
                            <div>
                                <p className={`${titleClass} font-medium text-muted-foreground uppercase tracking-wide`}>
                                    {t('recovery.reconciliation.maxDataGap', 'Maximum Data Gap')}
                                </p>
                                <p className={valueClass}>{formatHoursMinutes(stats.gapHours * 60)}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-muted/50">
                    <CardContent className={cardClass}>
                        <div className="flex items-center gap-2">
                            <Clock className={`${iconClass} text-muted-foreground shrink-0`} />
                            <div>
                                <p className={`${titleClass} font-medium text-muted-foreground uppercase tracking-wide`}>
                                    {t('recovery.reconciliation.latestBackupAny', 'Latest Backup (any store)')}
                                </p>
                                <p className={valueClass}>{fmt(new Date(stats.latestBackup))}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-muted/50">
                    <CardContent className={cardClass}>
                        <div className="flex items-center gap-2">
                            <ShieldAlert className={`${iconClass} text-muted-foreground shrink-0`} />
                            <div>
                                <p className={`${titleClass} font-medium text-muted-foreground uppercase tracking-wide`}>
                                    {t('recovery.reconciliation.scanDisposition', 'Scan Disposition')}
                                </p>
                                <p className={`${valueClass} flex flex-wrap items-center gap-x-2 gap-y-0.5`}>
                                    <span className="inline-flex items-center gap-1">
                                        <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500" />
                                        {stats.counts.detections} {t('recovery.reconciliation.detections', 'Detections')}
                                    </span>
                                    <span className="inline-flex items-center gap-1">
                                        <span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-500" />
                                        {stats.counts.indicators} {t('recovery.reconciliation.indicators', 'Indicators')}
                                    </span>
                                    <span className="inline-flex items-center gap-1">
                                        <span className="inline-block w-2.5 h-2.5 rounded-full bg-slate-400" />
                                        {stats.counts.noConfirmed} {t('recovery.reconciliation.clean', 'No confirmed detections')}
                                    </span>
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        )
    }

    // Analytics status dot component for table.
    // Code 2 = red (detections), code 5 = amber (indicators),
    // code 4 = NEUTRAL slate ("No confirmed detections" — not a clean bill of health).
    const StatusDot = ({ analytics }: { analytics: number | null }) => {
        const color = analytics === 2 ? 'bg-red-500 animate-analytics-pulse-red' : analytics === 5 ? 'bg-amber-500' : analytics === 4 ? 'bg-slate-400' : 'bg-gray-400'
        return <div className={`w-3 h-3 rounded-full ${color} inline-block`} />
    }

    // Versions detail table
    const renderVersionsTable = () => {
        if (!versionsData || versionsData.length === 0) {
            return (
                <div className="text-center text-muted-foreground py-8 text-sm">
                    {t('recovery.reconciliation.noVersionData', 'No version data available')}
                </div>
            )
        }

        return (
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b bg-muted/50">
                            <th className="text-left py-2 px-3 font-medium text-muted-foreground">{t('recovery.reconciliation.dataStore', 'Data Store')}</th>
                            <th className="text-left py-2 px-3 font-medium text-muted-foreground">{t('recovery.reconciliation.latestVersion', 'Latest Version')}</th>
                            <th className="text-center py-2 px-3 font-medium text-muted-foreground">{t('recovery.reconciliation.status', 'Status')}</th>
                            <th className="text-left py-2 px-3 font-medium text-muted-foreground">{t('recovery.reconciliation.latestDateTime', 'Latest DateTime')}</th>
                            <th className="text-left py-2 px-3 font-medium text-muted-foreground">{t('recovery.reconciliation.version', 'Version')}</th>
                            <th className="text-center py-2 px-3 font-medium text-muted-foreground">{t('recovery.reconciliation.status', 'Status')}</th>
                            <th className="text-left py-2 px-3 font-medium text-muted-foreground">{t('recovery.reconciliation.dateTime', 'Date/Time')}</th>
                            <th className="text-left py-2 px-3 font-medium text-muted-foreground">{t('recovery.reconciliation.dataLoss', 'Data Loss')}</th>
                            <th className="text-left py-2 px-3 font-medium text-muted-foreground">{t('recovery.reconciliation.latestNoConfirmed', 'Latest with no confirmed detections')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {versionsData.map((v) => {
                            const latestTs = parseTimestamp(v.latestDatetime)
                            const recomTs = parseTimestamp(v.recommendedDatetime)
                            const deltaMs = latestTs - recomTs
                            const deltaSecs = Math.floor(deltaMs / 1000)
                            const dDays = Math.floor(deltaSecs / 86400)
                            const dHrs = Math.floor((deltaSecs % 86400) / 3600)
                            const dMins = Math.floor((deltaSecs % 3600) / 60)
                            let deltaStr = ''
                            if (dDays > 0) deltaStr += `${dDays}d `
                            if (dHrs > 0) deltaStr += `${dHrs}h `
                            deltaStr += `${dMins}m`
                            if (deltaMs <= 0) deltaStr = '-'

                            return (
                                <tr key={v.sourceId} className="border-b hover:bg-muted/30">
                                    <td className="py-2 px-3">
                                        <div className="font-medium">{v.description}</div>
                                        <div className="text-xs text-muted-foreground">{v.sourceId}</div>
                                    </td>
                                    <td className="py-2 px-3 text-muted-foreground">{v.latestSecondary || '-'}</td>
                                    <td className="py-2 px-3 text-center"><StatusDot analytics={v.latestAnalytics} /></td>
                                    <td className="py-2 px-3 whitespace-nowrap">{v.latestDatetime ? fmt(new Date(parseTimestamp(v.latestDatetime))) : '-'}</td>
                                    <td className="py-2 px-3 text-muted-foreground font-medium">{v.recommendedSecondary || '-'}</td>
                                    <td className="py-2 px-3 text-center"><StatusDot analytics={v.recommendedAnalytics} /></td>
                                    <td className="py-2 px-3 whitespace-nowrap">{v.recommendedDatetime ? fmt(new Date(parseTimestamp(v.recommendedDatetime))) : '-'}</td>
                                    <td className="py-2 px-3">
                                        <span className={deltaMs > 0 ? 'text-amber-600 dark:text-amber-400 font-medium' : 'text-muted-foreground'}>
                                            {deltaStr}
                                        </span>
                                    </td>
                                    <td className="py-2 px-3 whitespace-nowrap" data-testid={`no-confirmed-${v.sourceId}`}>
                                        {(() => {
                                            const store = data?.find((d) => d.sourceId === v.sourceId)
                                            const ts = parseTimestamp(store?.recommendedGreenDateTime)
                                            if (!ts) return <span className="text-muted-foreground">{t('common.none', 'None')}</span>
                                            return (
                                                <span className="inline-flex items-center gap-1.5">
                                                    <span className="inline-block h-2 w-2 rotate-45" style={{ background: NO_CONFIRMED_MARKER }} />
                                                    {store?.recommendedGreenSecondary ? `${store.recommendedGreenSecondary} ` : ''}{fmt(new Date(ts))}
                                                </span>
                                            )
                                        })()}
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        )
    }

    // Render chart
    const renderChart = () => (
        <Card>
            <CardContent className="p-4">
                <div style={{ height: 420 }}>
                    <EChartsWrapper option={option} onEvents={onEvents} />
                </div>
            </CardContent>
        </Card>
    )

    return (
        <div className="space-y-4">
            {/* View Switcher */}
            <div className="flex items-center gap-1 border rounded-lg p-1 w-fit">
                <Button
                    variant={viewMode === 'chart' ? 'default' : 'ghost'}
                    size="sm"
                    className="h-8 gap-1.5"
                    onClick={() => setViewMode('chart')}
                >
                    <BarChart3 className={`h-4 w-4 ${viewMode !== 'chart' ? 'text-primary' : ''}`} />
                    {t('recovery.reconciliation.viewChart', 'Chart')}
                </Button>
                <Button
                    variant={viewMode === 'chartTable' ? 'default' : 'ghost'}
                    size="sm"
                    className="h-8 gap-1.5"
                    onClick={() => setViewMode('chartTable')}
                >
                    <LayoutList className={`h-4 w-4 ${viewMode !== 'chartTable' ? 'text-primary' : ''}`} />
                    {t('recovery.reconciliation.viewChartTable', 'Chart + Table')}
                </Button>
                <Button
                    variant={viewMode === 'table' ? 'default' : 'ghost'}
                    size="sm"
                    className="h-8 gap-1.5"
                    onClick={() => setViewMode('table')}
                >
                    <Table2 className={`h-4 w-4 ${viewMode !== 'table' ? 'text-primary' : ''}`} />
                    {t('recovery.reconciliation.viewTable', 'Table')}
                </Button>
                <Button
                    variant={viewMode === 'comparison' ? 'default' : 'ghost'}
                    size="sm"
                    className="h-8 gap-1.5"
                    onClick={() => setViewMode('comparison')}
                >
                    <Columns2 className={`h-4 w-4 ${viewMode !== 'comparison' ? 'text-primary' : ''}`} />
                    {t('recovery.reconciliation.viewComparison', 'Comparison')}
                </Button>
            </div>

            {/* Plain-language explanation of what the chart shows */}
            <p className="text-xs text-muted-foreground max-w-4xl">
                {t('recovery.reconciliation.caption',
                    'For a consistent recovery, every datastore must restore to the same moment — the reconciliation point (red line), the oldest of the datastores’ latest backups. Each bar is labelled with its backup version (type + date/time); amber is data beyond that point. Each datastore lists its versions and their scan disposition for your security team to choose from. Restore decisions rest with your security team; this is decision-support, not a determination that a version is free of threats.')}
            </p>

            {/* Summary Cards - shown for non-comparison views */}
            {viewMode !== 'comparison' && renderSummaryCards({ reconciliationPoint, latestBackup, gapHours, counts })}

            {/* Views */}
            {viewMode === 'chart' && renderChart()}

            {viewMode === 'chartTable' && (
                <>
                    {renderChart()}
                    <Card>
                        <CardContent className="p-4">
                            <h3 className="text-sm font-semibold mb-3">{t('recovery.reconciliation.versions', 'Per-version scan disposition')}</h3>
                            {renderVersionsTable()}
                        </CardContent>
                    </Card>
                </>
            )}

            {viewMode === 'table' && (
                <Card>
                    <CardContent className="p-4">
                        <h3 className="text-sm font-semibold mb-3">{t('recovery.reconciliation.versions', 'Per-version scan disposition')}</h3>
                        {renderVersionsTable()}
                    </CardContent>
                </Card>
            )}

            {viewMode === 'comparison' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="space-y-3">
                        <h3 className="text-sm font-semibold">{t('recovery.reconciliation.latestVersions', 'Latest Versions')}</h3>
                        {renderSummaryCards({ reconciliationPoint, latestBackup, gapHours, counts }, true)}
                        <Card>
                            <CardContent className="p-4">
                                <div style={{ height: 380 }}>
                                    <EChartsWrapper option={option} onEvents={onEvents} />
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                    <div className="space-y-3">
                        <h3 className="text-sm font-semibold">{t('recovery.reconciliation.versions', 'Per-version scan disposition')}</h3>
                        {versionStats && renderSummaryCards(versionStats, true)}
                        <Card>
                            <CardContent className="p-4">
                            {versionsData && versionsData.length > 0 ? (
                                <div style={{ height: 380 }}>
                                    <EChartsWrapper option={(() => {
                                        // Build a simpler chart plotting each version's date and its scan disposition
                                        const colors = buildThemeColors()
                                        const categories = versionsData.map((v) => v.description)
                                        const reconTs = parseTimestamp(versionsData[0]?.reconciliationDatetime)
                                        const timestamps = versionsData.map((v) => parseTimestamp(v.recommendedDatetime)).filter((t) => t > 0)
                                        if (timestamps.length === 0) return {}
                                        const minTs = Math.min(...timestamps)
                                        const maxTs = Math.max(...timestamps)
                                        const gap = maxTs - minTs
                                        const base = minTs - Math.max(gap * 0.3, 3600000)
                                        const ceil = maxTs + Math.max(gap * 0.1, 1800000)

                                        const hslToHex2 = (hslStr: string): string => {
                                            const el = document.createElement('div')
                                            el.style.color = hslStr
                                            document.body.appendChild(el)
                                            const computed = getComputedStyle(el).color
                                            document.body.removeChild(el)
                                            const match = computed.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/)
                                            if (match) return `#${parseInt(match[1]).toString(16).padStart(2, '0')}${parseInt(match[2]).toString(16).padStart(2, '0')}${parseInt(match[3]).toString(16).padStart(2, '0')}`
                                            return hslStr
                                        }

                                        const sHex = '#86efac'
                                        const sDark = '#4ade80'

                                        const reconData = versionsData.map((_v, i) => [i, base, reconTs > base ? reconTs : base])
                                        const aboveData = versionsData.map((v, i) => {
                                            const ts = parseTimestamp(v.recommendedDatetime)
                                            return ts > reconTs ? [i, reconTs, ts] : [i, reconTs, reconTs]
                                        })

                                        return {
                                            tooltip: {
                                                trigger: 'axis',
                                                axisPointer: { type: 'line', lineStyle: { color: colors.border, type: 'dashed' } },
                                                formatter: (params: any) => {
                                                    const items = Array.isArray(params) ? params : [params]
                                                    const item = items.find((p: any) => p.seriesName && !p.seriesName.startsWith('_'))
                                                    if (!item) return ''
                                                    const v = versionsData[item.dataIndex]
                                                    if (!v) return ''
                                                    const ts = parseTimestamp(v.recommendedDatetime)
                                                    return `<div style="padding:6px 10px">
                                                        <div style="font-weight:600;font-size:14px">${esc(v.description)}</div>
                                                        <div>${esc(v.recommendedSecondary || '-')}</div>
                                                        <div>Version: ${fmt(new Date(ts))}</div>
                                                    </div>`
                                                },
                                            },
                                            grid: { left: 20, right: 40, top: 20, bottom: 40, containLabel: true },
                                            xAxis: {
                                                type: 'category',
                                                data: categories,
                                                axisLabel: { color: colors.mutedForeground, fontSize: 11 },
                                                axisLine: { lineStyle: { color: colors.border } },
                                            },
                                            yAxis: {
                                                type: 'value',
                                                min: base,
                                                max: ceil,
                                                axisLabel: { color: colors.mutedForeground, fontSize: 11, formatter: (val: number) => fmt(new Date(val)) },
                                                axisLine: { show: false },
                                                splitLine: { lineStyle: { color: colors.border, type: 'dashed' } },
                                            },
                                            series: [
                                                {
                                                    name: 'Below recon',
                                                    type: 'custom',
                                                    renderItem: makeRenderItem(sHex, sDark, 'center', 'bottom') as any,
                                                    encode: { x: 0, y: [1, 2] },
                                                    data: reconData,
                                                    z: 2,
                                                },
                                                {
                                                    name: 'Above recon',
                                                    type: 'custom',
                                                    renderItem: makeRenderItem('#fcd34d', '#fbbf24', 'center', 'top') as any,
                                                    encode: { x: 0, y: [1, 2] },
                                                    data: aboveData,
                                                    z: 2,
                                                },
                                                {
                                                    name: '_gap_labels',
                                                    type: 'custom',
                                                    silent: true,
                                                    renderItem: ((_p: any, a: any) => {
                                                        const catIdx = a.value(0)
                                                        const ts = a.value(1)
                                                        const pt = a.coord([catIdx, ts])
                                                        const v = versionsData[catIdx]
                                                        const label = `${v?.recommendedSecondary ? v.recommendedSecondary + ' ' : ''}${fmt(new Date(ts))}`
                                                        return {
                                                            type: 'text',
                                                            style: {
                                                                text: label,
                                                                x: pt[0],
                                                                y: pt[1] - 6,
                                                                textAlign: 'center',
                                                                textVerticalAlign: 'bottom',
                                                                fill: colors.foreground,
                                                                fontWeight: 600,
                                                                fontSize: 11,
                                                            },
                                                        }
                                                    }) as any,
                                                    encode: { x: 0, y: 1 },
                                                    data: versionsData.map((v, i) => [i, parseTimestamp(v.recommendedDatetime)]),
                                                    z: 5,
                                                },
                                                {
                                                    name: '_recon',
                                                    type: 'line',
                                                    data: [],
                                                    markLine: {
                                                        silent: true,
                                                        symbol: 'none',
                                                        lineStyle: { color: hslToHex2(colors.destructive || 'hsl(0, 84%, 60%)'), type: 'dashed', width: 2 },
                                                        label: { show: false },
                                                        data: [{ yAxis: reconTs }],
                                                    },
                                                },
                                            ],
                                        }
                                    })()} />
                                </div>
                            ) : (
                                <div className="flex items-center justify-center h-[380px] text-muted-foreground text-sm">
                                    {t('recovery.reconciliation.noVersionData', 'No version data available')}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                    </div>
                </div>
            )}
        </div>
    )
}
